// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { css } from "styled-components";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import parse from "html-react-parser";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { spacingStyles } from "@toolkit/Themes/common/mixins";

export interface SignInTermsAndConditionsStyles {
    gridItem?: GridItemProps;
    wrapper?: InjectableCss;
    headerRow?: InjectableCss;
    titleText?: TypographyProps;
    requiredText?: TypographyProps;
    content?: InjectableCss;
    contentCollapsed?: InjectableCss;
    link?: LinkPresentationProps;
    checkboxGroup?: CheckboxGroupProps;
    checkbox?: CheckboxProps;
}

export const signInTermsAndConditionsStyles: SignInTermsAndConditionsStyles = {
    gridItem: {
        width: 12,
    },
    wrapper: {
        css: css`
            margin-top: ${pxToRem(12)};
        `,
    },
    headerRow: {
        css: css`
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: ${pxToRem(12)};
        `,
    },
    titleText: { variant: "h6", as: "p" },
    requiredText: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    content: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            ${spacingStyles(pxToRem(15), pxToRem(15))};
        `,
    },
    contentCollapsed: {
        css: css`
            position: relative;
            overflow: hidden;
            max-height: calc(4 * ${x => x.theme.design.typography.lineHeight.base}em);

            &::after {
                content: "";
                position: absolute;
                left: 0;
                right: 0;
                bottom: 0;
                height: calc(4 * ${x => x.theme.design.typography.lineHeight.base}em);
                pointer-events: none;
                background: linear-gradient(to bottom, rgba(255, 255, 255, 0), ${getColor("common.background")});
            }
        `,
    },
    link: {
        css: css`
            ${linkStyles("text.link", "text.linkHover", true, true)}
            ${spacingStyles(pxToRem(15), pxToRem(15))};
        `,
    },
    checkboxGroup: {
        css: css`
            ${spacingStyles(pxToRem(15), null)}
            &:last-child {
                margin-bottom: ${pxToRem(15)};
            }
        `,
    },
};

export interface SignInTermsAndConditionsProps {
    enabled: boolean;
    title?: string;
    linkText?: string;
    hideLinkText?: string;
    checkboxLabel?: string;
    contentHtml?: string;
    accepted: boolean;
    onAcceptedChange: (value: boolean) => void;
    extendedStyles?: SignInTermsAndConditionsStyles;
}

const SignInTermsAndConditions = ({
    enabled,
    title,
    linkText,
    hideLinkText,
    checkboxLabel,
    contentHtml,
    accepted,
    onAcceptedChange,
    extendedStyles,
}: SignInTermsAndConditionsProps) => {
    const [styles] = useState(() => mergeToNew(signInTermsAndConditionsStyles, extendedStyles));
    const [isExpanded, setIsExpanded] = useState(false);

    if (!enabled) {
        return null;
    }

    const displayTitle = title || translate("Terms & Conditions");
    const displayLink = linkText || translate("Open and read all Terms & Conditions");
    const displayHideLink = hideLinkText || translate("Hide Terms & Conditions");
    const displayCheckbox = checkboxLabel || translate("I have read and accept the Terms & Conditions *");
    const hasContent = !!(contentHtml && contentHtml.trim());

    return (
        <GridItem {...styles.gridItem}>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.headerRow}>
                    <Typography {...styles.titleText}>{displayTitle}</Typography>
                    <Typography {...styles.requiredText}>* {translate("Required Fields")}</Typography>
                </StyledWrapper>
                {hasContent && (
                    <>
                        <StyledWrapper
                            {...styles.content}
                            css={css`
                                ${styles.content?.css as any}
                                ${!isExpanded ? (styles.contentCollapsed?.css as any) : ""}
                            `}
                        >
                            {parse(contentHtml || "", parserOptions)}
                        </StyledWrapper>
                        <Link
                            {...styles.link}
                            type="button"
                            onClick={() => setIsExpanded(prev => !prev)}
                            aria-expanded={isExpanded}
                            aria-controls="signInTermsContent"
                        >
                            {isExpanded ? displayHideLink : displayLink}
                        </Link>
                    </>
                )}
                <CheckboxGroup {...styles.checkboxGroup}>
                    <Checkbox
                        uid="termsAccepted"
                        {...styles.checkbox}
                        checked={accepted}
                        onChange={(_, v) => onAcceptedChange(v)}
                    >
                        {displayCheckbox}
                    </Checkbox>
                </CheckboxGroup>
            </StyledWrapper>
        </GridItem>
    );
};

export default SignInTermsAndConditions;
